import React from "react";
import {CustomTextField} from "../../../../../../components/CustomTextField";
import CustomDateTimePicker from "../../../../../../components/CustomDateTimePicker";
import {isNotEmpty} from "../../../../../../Apis/functions";
import {getMovementDirectionOption} from "../../../Model/Model";
import { PERIOD_TIME_UNIT_ENUM } from "../../../../../../enums/PeriodTimeUnitEnum";

/**
 * Fields of the movement of a live poultry truck.
 *
 * The platforms are read at the gate, so they are not required for the
 * requester; the observations travel with the folio and are optional for
 * whoever captures it.
 */
export const MOVEMENT_FIELDS = [
    {
        id: 'txt_lptms_platform_1_no',
        name: 'platform1No',
        trans: 'platform_1_no',
        placeholder: 'enter_unit_number',
        className: 'col-sm-12 col-md-6',
        length: 128
    },
    {
        id: 'txt_lptms_platform_2_no',
        name: 'platform2No',
        trans: 'platform_2_no',
        placeholder: 'enter_unit_number',
        className: 'col-sm-12 col-md-6',
        length: 128
    },
    {
        id: 'txt_lptms_observations',
        name: 'observations',
        // The folio stores the observations as its extra details
        registeredName: 'extraDetails',
        trans: 'observations',
        placeholder: 'add_any_relevant_notes_or_observations',
        className: 'col-12',
        length: 512,
        multiline: true
    }
];

/**
 * Fields of the movement as the guard captures them at the gate.
 *
 * What the requester already registered is only read, and what is left blank is
 * written by the guard: the platforms are required, because they are read from
 * the vehicle, and the observations stay optional.
 *
 * @param {Object} item - Folio the movement belongs to.
 *
 * @returns {Array} Fields of the movement as the gate captures them.
 */
export const getGuardMovementFields = (item) => MOVEMENT_FIELDS.map((field) => {
    const disabled = isNotEmpty(item?.[field.registeredName || field.name]);

    return {...field, disabled, required: !disabled && !field.multiline};
});

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @description
 * Renders the movement of a live poultry truck form: the date on which the
 * direction is expected, the platforms that carry the poultry and the
 * observations of the folio. The card is titled with the direction that was
 * selected, so it is only displayed once the movement has one.
 *
 * @param {Function} t - Translation function.
 * @param {Object} materialRequest - Current form information.
 * @param {Function} handleChange - Change handler of the form.
 * @param {boolean} emptyFields - Indicates whether the required fields must be highlighted.
 * @param {Array} fields - Configuration of the fields being captured.
 * @param {boolean} required - Indicates whether the date of the movement has to be
 * captured, because the requester leaves it to the guard and the guard reads it at the gate.
 * @param {boolean} disablePast - Blocks the dates and times that already went by,
 * so the date of the movement is never registered in the past, neither by the
 * requester nor by the guard at the gate.
 * @param {boolean} disabled - Indicates whether the whole movement is only read.
 * @param {boolean} dateRegistered - Indicates whether the folio already carries
 * the date of the movement, so it is only read and the gate does not write it again.
 * @param {string} contentClassName - Spacing the values are read with inside the block.
 */
const LivePoultryTruckMovementSection = ({
    t,
    materialRequest,
    handleChange,
    emptyFields,
    fields = MOVEMENT_FIELDS,
    required = true,
    disablePast = false,
    disabled = false,
    dateRegistered = false,
    contentClassName = 'row py-3'
}) => {

    const isDateDisabled = disabled || dateRegistered;
    const isDateRequired = required && !isDateDisabled;

    const selectedDirectionOption = getMovementDirectionOption(materialRequest?.movementDirection);

    if (!selectedDirectionOption)
        return null;

    const SelectedDirectionIcon = selectedDirectionOption.Icon;

    return (
        <div className="border-full-shades-300 background-general-00 border-radius-8px my-3">
            <div className={"d-flex align-items-center p-2"}>
                <div className={`mr-2 ${selectedDirectionOption.iconBackground} p-2 border-radius-8px d-flex align-items-center`}>
                    <SelectedDirectionIcon className={selectedDirectionOption.iconColor}/>
                </div>
                <p className={"fs-14px ibl-font-w-600"}>{t(selectedDirectionOption.trans)}</p>
            </div>
            <div className={"border-bottom-shades-300"}/>

            <div className={contentClassName}>
                <div className={"col-12"}>
                    <CustomDateTimePicker
                        id={`dp_lptms_${selectedDirectionOption.dateTrans}`}
                        name={selectedDirectionOption.dateName}
                        onChange={(e) => {
                            handleChange({target: {value: e, name: selectedDirectionOption.dateName}});
                        }}
                        trans={selectedDirectionOption.dateTrans}
                        value={materialRequest?.[selectedDirectionOption.dateName]}
                        required={isDateRequired}
                        disabled={isDateDisabled}
                        openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                        disablePast={disablePast}
                        emptyFields={emptyFields && isDateRequired}
                    />
                </div>

                {fields.map(({id, name, trans, placeholder, className, length, multiline = false, required: fieldRequired = false, disabled: fieldDisabled = false}) =>
                    <div className={className} key={name}>
                        <CustomTextField
                            id={id}
                            length={length}
                            name={name}
                            value={materialRequest?.[name]}
                            trans={trans}
                            type={'text'}
                            disabled={disabled || fieldDisabled}
                            onChange={handleChange}
                            emptyFields={emptyFields && !disabled && fieldRequired}
                            required={!disabled && fieldRequired}
                            multiline={multiline}
                            row={multiline ? 4 : undefined}
                            placeholder={t(placeholder)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LivePoultryTruckMovementSection;
